import { suggestionsQueryOptions } from "@/features/search/api/queries";
import type { Suggestion } from "@/features/search/api/types";
import { useDebouncedValue } from "@/features/search/hooks/useDebouncedValue";
import { useContentLocale } from "@/i18n/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

const DEBOUNCE_MS = 200;

/** A single letter matches half the catalogue — wait for a prefix worth completing. */
const MIN_TERM_LENGTH = 2;

const NO_SUGGESTIONS: Suggestion[] = [];

/**
 * Query suggestions for `term`. An empty term is a valid request — the service answers it with the
 * trending queries, which is what the dropdown shows before the user types.
 *
 * `enabled` is the caller's: nothing is requested until the dropdown is actually open, so the header
 * costs no requests on a page the visitor never searches from.
 */
export const useSuggestions = (term: string, enabled: boolean) => {
  // The service's `lang` is a content language: `/en-us` reads `en` (WHO-5766).
  const locale = useContentLocale();
  const debouncedTerm = useDebouncedValue(term.trim(), DEBOUNCE_MS);

  const requestable =
    !debouncedTerm.length || debouncedTerm.length >= MIN_TERM_LENGTH;

  const { data } = useQuery({
    ...suggestionsQueryOptions({ query: debouncedTerm, lang: locale }),
    enabled: enabled && requestable,
    /* Hold the previous prefix's list while the next one loads — an empty flash between keystrokes
     * reads as "nothing found". */
    placeholderData: keepPreviousData,
  });

  return data?.data ?? NO_SUGGESTIONS;
};
